import { Box, Typography } from "@mui/material"
import type { FC } from "react"

import { StatusIndicator } from "@/components/common/StatusIndicator/StatusIndicator"
import { LongWordWrapper } from "@/components/table/LongWordWrapper"
import { ComputedProductStatusMap } from "@/screens/Companies/products/utils/productStatusMap"
import { CasNumbersCell } from "@/screens/ManageProducts/components/CompetitorProducts/components/CasNumbersCell"
import TableCellWithId from "@/screens/ManageProducts/components/Instructions/components/tables/TableCellWithId"
import { type AssociatedProduct, getCasNumbers, getProductName, isAkrochemProduct } from "@/utils/equivalentProducts"

interface AlternateProductCellProps {
  product: AssociatedProduct
}

/**
 * Displays product information for both Akrochem and Competitor products
 * Shows product name, chemical name, and CAS numbers with appropriate formatting
 */
export const AlternateProductCell: FC<AlternateProductCellProps> = ({ product }) => {
  const isAkrochem = isAkrochemProduct(product)
  const productName = getProductName(product)
  const chemicalName = product.chemicalName
  const casNumbers = getCasNumbers(product)

  return (
    <Box className='flex flex-col'>
      <Box className='flex gap-2'>
        <Box className='mr-2 flex w-2 justify-end pt-3'>
          {isAkrochem && (
            <StatusIndicator
              data-testid={`statusIndicator-${product.computedProductStatus}`}
              status={product.computedProductStatus as string}
              map={ComputedProductStatusMap}
            />
          )}
        </Box>
        <Box>
          {isAkrochem ? (
            <TableCellWithId id={product.externalSystemId}>
              <Typography className='text-sm text-gray-900'>{productName}</Typography>
            </TableCellWithId>
          ) : (
            <Typography className='text-sm text-gray-900'>{productName}</Typography>
          )}
          {chemicalName && (
            <Typography className='text-xs text-gray-900'>
              <LongWordWrapper>[{chemicalName}]</LongWordWrapper>
            </Typography>
          )}

          {!!casNumbers.length && (
            <Box className='flex items-center gap-1'>
              <Typography className='text-sm text-gray-900'>CAS#</Typography>{" "}
              <CasNumbersCell casNumbers={casNumbers} className='text-gray-900' />
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  )
}
